import React, { useState, useEffect } from 'react';
import { Timer, Save, Clock, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { settingsAPI } from '../../services/api';

const ChecklistMonitorSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ checklist_monitor_hours: 48, notification_repeat_minutes: 10 });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  useEffect(() => {
    const load = async () => {
      try {
        const res = await settingsAPI.getChecklistMonitorSettings();
        setSettings(res.data);
      } catch (e) { console.error('Error loading monitor settings:', e); }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateChecklistMonitorSettings(settings);
      setMessage('Settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (e) {
      setMessage(e.response?.status === 403 ? 'Only admins can change this setting' : 'Error saving settings');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="checklist-monitor-settings">
      <div>
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Timer className="text-teal-600" size={24} />
          Checklist Monitoring
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure the countdown timer for Closed Won leads. After this period, if any checklist items remain pending, admin will receive recurring notifications.
        </p>
      </div>
      
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Monitoring Duration</label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              data-testid="monitor-hours-input"
              value={settings.checklist_monitor_hours}
              onChange={(e) => setSettings(s => ({ ...s, checklist_monitor_hours: parseInt(e.target.value) || 48 }))}
              min={1}
              max={720}
              disabled={!isAdmin}
              className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
            />
            <span className="text-sm text-gray-500">hours after Closed Won (default: 48)</span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Notification Repeat Interval</label>
          <div className="flex items-center gap-3">
            <input
              type="number"
              data-testid="repeat-minutes-input"
              value={settings.notification_repeat_minutes}
              onChange={(e) => setSettings(s => ({ ...s, notification_repeat_minutes: parseInt(e.target.value) || 10 }))}
              min={1}
              max={1440}
              disabled={!isAdmin}
              className="w-32 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
            />
            <span className="text-sm text-gray-500">minutes between notifications (default: 10)</span>
          </div>
        </div>

        {!isAdmin && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700">Only administrators can modify these settings.</p>
          </div>
        )}
        
        {isAdmin && (
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-monitor-settings"
            className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors font-medium text-sm"
          >
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
        
        {message && (
          <p className={`text-sm font-medium ${message.includes('success') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <Clock size={16} className="text-gray-500" />
          Monitored Checklist Fields
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {['NOC Agreement', 'Electricity Bill', 'PAN Card', 'Aadhar Card', 'Property Photos', 'Collection Status', 'Master Form'].map(field => (
            <div key={field} className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500"></div>
              {field}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChecklistMonitorSettings;
